<!--
 * @Author: Tahiti
 * @Date: 2025-12-17
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-17
 * @Description: Photo Effects Page - 图片特效页面（网格布局，无Tab）
 * @FilePath: /weryai-web/pages/photo-effects/index.vue
-->
<template>
    <div class="image-effects-page">
        <!-- 顶部轮播 -->
        <div class="mb-10">
            <ClientOnly>
                <EffectsCarousel
                    :items="carouselItems"
                    type="IMAGE"
                    @use-template="handleUseTemplate"
                    @item-click="handleUseTemplate"
                />
                <template #fallback>
                    <div class="flex gap-5 overflow-hidden">
                        <div v-for="i in 3" :key="i" class="flex-shrink-0">
                            <div
                                class="w-full aspect-[2/1] md:w-[632px] md:h-[316px] md:aspect-auto rounded-xl bg-[#1b1d25] animate-pulse"
                            ></div>
                            <div
                                class="h-5 w-48 bg-[#1b1d25] rounded mt-5"
                            ></div>
                        </div>
                    </div>
                </template>
            </ClientOnly>
        </div>

        <!-- 标题 -->
        <h1
            class="section-title transition-all duration-300"
            :style="{
                marginLeft:
                    isClient && layoutInfo.offsetX > 0
                        ? `${layoutInfo.offsetX}px`
                        : '0px',
            }"
        >
            {{ t('effects.aiPhotoEffectsTitle') }}
        </h1>

        <!-- 图片特效网格区域 -->
        <div
            ref="containerRef"
            class="image-effects-grid"
            :class="{ 'is-ready': isLayoutReady }"
            :style="{ height: `${containerHeight}px` }"
        >
            <!-- 只有在布局准备好后才渲染卡片，避免闪烁 -->
            <template v-if="isLayoutReady">
                <div
                    v-for="item in gridItems"
                    :key="item.id"
                    class="grid-item absolute transition-all duration-300"
                    :style="item.style"
                >
                    <PhotoEffectsCard
                        :item="item"
                        :width="columnWidth"
                        :height="itemHeight"
                        @click="handleCardClick"
                        @use-template="handleUseTemplate"
                    />
                </div>
            </template>
        </div>

        <!-- 加载更多触发器（IntersectionObserver 监听） -->
        <div ref="loadMoreSentinel" class="load-more-sentinel h-1"></div>

        <!-- 加载更多 -->
        <div v-if="loading" class="loading-indicator flex justify-center py-8">
            <l-dot-spinner
                v-if="isClient"
                size="30"
                speed="0.9"
                color="var(--el-menu-active-color)"
            ></l-dot-spinner>
        </div>

        <!-- 没有更多数据 -->
        <div
            v-if="!hasMore && items.length > 0"
            class="no-more-data flex justify-center py-6 text-gray-400 text-sm"
        >
            {{ t('inspiration.noMoreData') }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { definePageMeta, navigateTo, useAsyncData, useRoute } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import {
    getEffectTemplatePageApi,
    getEffectTemplateTopApi,
    getEffectTemplateLastUpdateApi,
} from '~/api/effects'
import { storage } from '~/utils/localstorage'
import EffectsCarousel from '~/components/Effects/EffectsCarousel.vue'
import PhotoEffectsCard from '~/components/Effects/PhotoEffectsCard.vue'

definePageMeta({
    layout: 'laboratory',
})

useMultilingualSeo({ pageType: 'photoEffects' })

const { t, locale } = useI18n()
const route = useRoute()

/**
 * 补齐“当前 URL 语言模式”的基础前缀
 * - /zh/... => /zh
 * - /en/... => /en（英文前缀模式）
 * - 英文无前缀 => ''
 *
 * 说明：这里优先读 address bar 的 pathname，避免 /en alias 场景下 route.path 丢前缀。
 */
const getUrlBasePrefix = () => {
    const supported = [
        'zh',
        'en',
        'ja',
        'ko',
        'es',
        'fr',
        'de',
        'ru',
        'pt',
        'it',
        'ar',
    ]
    const p =
        import.meta.client && typeof window !== 'undefined'
            ? window.location.pathname
            : route.path || '/'
    const m = p.match(/^\/([a-z]{2})(\/|$)/)
    const maybe = m?.[1]
    if (maybe && supported.includes(maybe)) {
        return maybe === 'en' ? '/en' : `/${maybe}`
    }
    return ''
}

const withUrlPrefix = toPath => {
    const base = getUrlBasePrefix()
    if (!base) return toPath
    if (toPath === '/') return base
    return `${base}${toPath}`
}

// 缓存配置（使用后端时间戳判断是否需要刷新缓存）
const CACHE_KEYS = {
    IMAGE_LIST: (lang, page) => `effects_image_list_${lang}_${page}`,
    IMAGE_TIMESTAMP: lang => `effects_image_timestamp_${lang}`, // 存储后端数据最后更新时间戳
}

// 网格配置
const config = {
    baseColumnWidth: 240,
    maxColumnWidth: 300,
    minColumnWidth: 140, // 下调最小宽度，确保移动端两列能放下
    gap: 16,
    minColumns: 2,
    titleHeight: 44, // 标题区域高度
}

const containerRef = ref(null)
const containerWidth = ref(0)
const columnCount = ref(4)
const columnWidth = ref(config.baseColumnWidth)
const loading = ref(false)
const isLayoutReady = ref(false) // 布局是否准备好
const isClient = ref(false) // 是否客户端渲染

// 分页相关
const currentPage = ref(1)
const pageSize = 20
const hasMore = ref(true)

// 卡片高度 = 宽度（正方形卡片，PhotoEffectsCard 内部已处理好图片+标题的高度分配）
const itemHeight = computed(() => columnWidth.value)

// 计算列数和列宽
const calculateColumns = () => {
    if (!containerRef.value) return

    const width_val = containerRef.value.offsetWidth
    containerWidth.value = width_val

    let columns = Math.floor(
        (width_val + config.gap) / (config.baseColumnWidth + config.gap)
    )
    columns = Math.max(columns, config.minColumns)

    let width = (width_val - (columns - 1) * config.gap) / columns
    width = Math.max(
        config.minColumnWidth,
        Math.min(config.maxColumnWidth, width)
    )

    columnCount.value = columns
    columnWidth.value = Math.floor(width) // 取整避免亚像素偏移
    isLayoutReady.value = true // 标记布局已准备好
}

// 模板列表数据
const items = ref([])

// 轮播置顶列表数据
const carouselItems = ref([])

// 加载置顶列表（不缓存，运营数据需实时更新）
const loadTopList = async () => {
    try {
        const res = await getEffectTemplateTopApi({
            type: 'IMAGE',
            lang: locale.value,
        })
        if (res.status === 200 && res.data) {
            carouselItems.value = res.data.map(item => ({
                cover: item.cover_url,
                mask: '',
                desc: item.name,
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
            }))
        }
    } catch (error) {
        console.error('加载置顶列表失败:', error)
    }
}

// 清除所有分页缓存
const clearAllPageCache = lang => {
    // 清除所有分页缓存（假设最多缓存 50 页）
    for (let i = 1; i <= 50; i++) {
        storage.remove(CACHE_KEYS.IMAGE_LIST(lang, i))
    }
}

// 加载图片特效模板列表（使用后端时间戳判断是否需要刷新缓存）
const loadTemplates = async (page = 1, append = false) => {
    if (loading.value) return
    loading.value = true

    try {
        const cacheKey = CACHE_KEYS.IMAGE_LIST(locale.value, page)
        const timestampKey = CACHE_KEYS.IMAGE_TIMESTAMP(locale.value)

        // 1. 先请求时间戳接口，获取后端数据最后更新时间
        let serverTimestamp = 0
        try {
            const timestampRes = await getEffectTemplateLastUpdateApi('IMAGE')
            serverTimestamp = timestampRes.data || 0
        } catch (err) {
            console.warn('获取时间戳失败，将重新请求数据:', err)
        }

        // 2. 获取本地缓存的时间戳
        const localTimestamp = storage.get(timestampKey) || 0

        // 3. 判断是否可以使用缓存：后端时间戳有效(>0) 且 等于本地时间戳
        const canUseCache =
            serverTimestamp > 0 && serverTimestamp === localTimestamp

        // 4. 如果不能使用缓存且本地有旧缓存，清除所有分页缓存
        if (!canUseCache && localTimestamp > 0) {
            console.log('[photo-effects] 时间戳变化或无效，清除所有分页缓存')
            clearAllPageCache(locale.value)
        }

        // 更新本地时间戳（只在后端时间戳有效且与本地不一致时更新）
        if (serverTimestamp > 0 && serverTimestamp !== localTimestamp) {
            storage.set(timestampKey, serverTimestamp)
        }

        // 5. 获取缓存数据
        const cached = storage.get(cacheKey)

        // 6. 如果可以使用缓存且有缓存数据，直接使用
        if (canUseCache && cached) {
            console.log('[photo-effects] 时间戳一致，使用缓存')
            if (append) {
                items.value = [...items.value, ...cached.items]
            } else {
                items.value = cached.items
            }
            hasMore.value = cached.hasMore
            currentPage.value = page
            loading.value = false
            return
        }

        // 7. 其他情况（时间戳为0/null或不一致或无缓存）：请求接口
        console.log('[photo-effects] 请求数据，page:', page)
        const res = await getEffectTemplatePageApi({
            type: 'IMAGE',
            pageNum: page,
            pageSize: pageSize,
            lang: locale.value,
        })

        if (res.status === 200 && res.data?.content) {
            const newItems = res.data.content.map(item => ({
                id: String(item.id),
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
                cover: item.cover_url,
                title: item.name,
                likes: 0,
                type: item.type,
                tags: item.tags,
            }))

            if (append) {
                items.value = [...items.value, ...newItems]
            } else {
                items.value = newItems
            }

            // 判断是否有更多数据
            const hasMoreData = newItems.length >= pageSize
            hasMore.value = hasMoreData
            currentPage.value = page

            // 8. 更新缓存（不设过期时间，由时间戳判断是否刷新）
            storage.set(cacheKey, { items: newItems, hasMore: hasMoreData })
        } else {
            // 请求失败时，如果是首次加载则清空，追加模式则标记无更多
            if (!append) {
                items.value = []
            }
            hasMore.value = false
        }
    } catch (error) {
        console.error('加载图片特效模板失败:', error)
        hasMore.value = false
    } finally {
        loading.value = false
        // 检查是否需要继续加载更多（大屏幕场景）
        nextTick(() => {
            checkAndLoadMore()
        })
    }
}

// 加载更多
const loadMore = () => {
    if (loading.value || !hasMore.value) return
    loadTemplates(currentPage.value + 1, true)
}

// IntersectionObserver 用于无限滚动加载（不阻塞主线程）
let loadMoreObserver = null
const loadMoreSentinel = ref(null)

// 检查是否需要自动加载更多（大屏幕场景：内容未填满视口时自动加载）
const checkAndLoadMore = () => {
    if (loading.value || !hasMore.value || !loadMoreSentinel.value) return

    const rect = loadMoreSentinel.value.getBoundingClientRect()
    // 如果 sentinel 在视口内（加 200px margin），继续加载
    if (rect.top < window.innerHeight + 200) {
        loadMore()
    }
}

// 网格布局计算
// ========== 网格布局计算（高度自适应） ==========
const layoutInfo = computed(() => {
    if (containerWidth.value <= 0) return { offsetX: 0, totalWidth: 0 }
    const totalWidth =
        columnCount.value * columnWidth.value +
        (columnCount.value - 1) * config.gap
    const offsetX = Math.floor(
        Math.max(0, (containerWidth.value - totalWidth) / 2)
    )
    return { offsetX, totalWidth }
})

const gridItems = computed(() => {
    if (!containerRef.value) return []

    const { offsetX } = layoutInfo.value
    const totalItemHeight = itemHeight.value + config.gap
    const heights = new Array(columnCount.value).fill(0)

    return items.value.map(item => {
        const colIndex = heights.indexOf(Math.min(...heights))

        const left = colIndex * (columnWidth.value + config.gap) + offsetX
        const top = heights[colIndex]

        heights[colIndex] += totalItemHeight

        return {
            ...item,
            style: {
                left: `${left}px`,
                top: `${top}px`,
            },
        }
    })
})

// 容器高度
const containerHeight = computed(() => {
    if (gridItems.value.length === 0) return 400

    const totalItemHeight = itemHeight.value + config.gap
    const heights = new Array(columnCount.value).fill(0)
    items.value.forEach(() => {
        const colIndex = heights.indexOf(Math.min(...heights))
        heights[colIndex] += totalItemHeight
    })

    return Math.max(...heights, 400)
})

// 点击卡片
const handleCardClick = item => {
    // 点击卡片跳转到详情页（和使用模板相同）
    handleUseTemplate(item)
}

// 处理使用模板
const handleUseTemplate = item => {
    console.log('使用模板:', item)
    // 生成模板 slug（将标题转为 URL 友好格式）
    const slug =
        item.slug || item.title?.toLowerCase().replace(/\s+/g, '-') || item.id
    // 关键：直接跳到带语言前缀的地址，避免全局中间件 replace 导致历史栈异常
    navigateTo(withUrlPrefix(`/photo-effects/${slug}`))
}

// 监听窗口大小变化
let resizeObserver = null

onMounted(() => {
    isClient.value = true
    analyticsEvent('page_view_pic_effects')

    calculateColumns() // 首次即时计算

    if (containerRef.value) {
        resizeObserver = new ResizeObserver(() => {
            calculateColumns()
        })
        resizeObserver.observe(containerRef.value)
    }

    // 加载置顶列表
    loadTopList()

    // 加载模板数据
    loadTemplates()

    // 使用 IntersectionObserver 监听加载更多（不阻塞主线程）
    nextTick(() => {
        if (loadMoreSentinel.value) {
            loadMoreObserver = new IntersectionObserver(
                entries => {
                    if (
                        entries[0].isIntersecting &&
                        !loading.value &&
                        hasMore.value
                    ) {
                        loadMore()
                    }
                },
                { rootMargin: '200px' }
            )

            loadMoreObserver.observe(loadMoreSentinel.value)
        }
    })
})

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
    }

    if (loadMoreObserver) {
        loadMoreObserver.disconnect()
    }
})

// 监听数据变化
watch(
    () => items.value,
    () => {
        nextTick(() => {
            calculateColumns()
        })
    },
    { deep: true }
)
</script>

<style lang="less" scoped>
@import '@/assets/css/mixins.less';

.image-effects-page {
    min-height: calc(100vh - 52px);
    width: 100%;
    padding: 0;
    padding-bottom: 30px;
}

.section-title {
    color: #fff;
    font-family: Roboto;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 20px;
}

.image-effects-grid {
    position: relative;
    min-height: 400px;
    opacity: 0; // 默认隐藏，避免布局闪烁

    &.is-ready {
        opacity: 1; // 准备好后显示
        transition:
            opacity 0.2s ease,
            height 0.3s ease;
    }
}

.grid-item {
    transition:
        left 0.3s ease,
        top 0.3s ease;
}
</style>
